import { Button, Group, Input, Paper, Popover, Title } from '@mantine/core';
import {
  DatePickerInput,
  DatesRangeValue,
  YearPickerInput,
} from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { IconCalendar, IconSelector } from '@tabler/icons-react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CreateOrderForm,
  CreateOrderFormValues,
} from '../../components/forms/create-order-form';
import { OrderFiltersMenu } from '../../components/orders-filters-menu/filters';
import { FiltersButton } from '../../components/orders-filters-menu/filters-button';
import { OrdersTimeline } from '../../components/orders-timeline';
import { samplingApi } from '../../data-access/apis/sampling';
import { useSamplingQueries } from '../../data-access/queries/sampling';

import { formatDate, ordersYearAtom, useColFilter } from '@data-access';
import { Drawer, FullMainHeight } from '@ui';

export const OrdersPage: FC = () => {
  const { t } = useTranslation();
  const [year, setYear] = useAtom(ordersYearAtom);
  const [opened, actions] = useDisclosure(false);
  const [dateFilterValue, setDateFilterValue, resetDateFilter] =
    useColFilter<DatesRangeValue>('orders', 'date');

  const samplingQueries = useSamplingQueries();
  const filters = useQuery({
    ...samplingQueries.orders.filters(),
    placeholderData: keepPreviousData,
  });

  const createOrder = useMutation({
    mutationFn: samplingApi.orders.create,
    onSuccess: () => {
      actions.close();
    },
  });

  const handleYearChange = (val: Date | null) => {
    setYear(val ? val.getFullYear() : year);
  };

  const handleSubmit = (values: CreateOrderFormValues) => {
    createOrder.mutate(values);
  };

  return (
    <>
      <FullMainHeight.Root>
        <FullMainHeight.Header>
          <Paper>
            <Group justify="space-between">
              <Group>
                <Title order={2}>{t('common.orders')}</Title>
                <YearPickerInput
                  fw="bold"
                  onChange={handleYearChange}
                  rightSection={<IconSelector size={14} />}
                  rightSectionPointerEvents="none"
                  value={year ? dayjs().set('year', year).toDate() : undefined}
                  valueFormat="YYYY"
                />
                <DatePickerInput
                  color="gray"
                  leftSection={<IconCalendar size={14} />}
                  onChange={(value) => {
                    setDateFilterValue(value);
                  }}
                  placeholder={t('common.periode-de-visualisation')}
                  rightSection={
                    dateFilterValue?.length ? (
                      <Input.ClearButton onClick={() => resetDateFilter()} />
                    ) : undefined
                  }
                  type="range"
                  value={(dateFilterValue as DatesRangeValue) || [null, null]}
                  valueFormatter={({ date }) =>
                    Array.isArray(date) && date[0]
                      ? `${formatDate({ date: date[0], format: 'display' }) || ''} - ${formatDate({ date: date[1], format: 'display' }) || ''}`
                      : ''
                  }
                />
                <Popover position="bottom-start" width={400}>
                  <Popover.Target>
                    <FiltersButton />
                  </Popover.Target>
                  <Popover.Dropdown>
                    {filters.data ? (
                      <OrderFiltersMenu filters={filters.data} />
                    ) : null}
                  </Popover.Dropdown>
                </Popover>
              </Group>

              <Group>
                <Button onClick={actions.open}>
                  {t('sampling.ajouter-une-commande')}
                </Button>
              </Group>
            </Group>
          </Paper>
        </FullMainHeight.Header>
        <FullMainHeight.Content>
          <OrdersTimeline />
        </FullMainHeight.Content>
      </FullMainHeight.Root>
      <Drawer
        onClose={actions.close}
        opened={opened}
        position="right"
        size="lg"
        title={t('sampling.creer-un-plan')}
      >
        <CreateOrderForm onCancel={actions.close} onSubmit={handleSubmit} />
      </Drawer>
    </>
  );
};
